import { createContext, useContext, useRef } from 'react';
import type { ReactNode, RefObject } from 'react';

// The app scrolls inside a single fixed-height element (`.app-scroll`)
// instead of the document. Keeping the document itself unscrolled is what
// fixes the iOS WebView View Transitions flash: WebKit captures the
// `::view-transition-old(root)` snapshot from the document's scroll-top, so
// a scrolled *document* showed the leaving page at the wrong offset for a
// frame. With the document pinned at 0 and all scrolling delegated to this
// container, the captured snapshot always matches what is on screen.
//
// This context shares the container ref so the scroll-dependent hooks
// (restoration) and per-page scroll listeners can target the same element
// without prop-drilling or DOM id lookups.

type ScrollContainerRef = RefObject<HTMLDivElement | null>;

const ScrollContainerContext = createContext<ScrollContainerRef | null>(null);

export const ScrollContainerProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement | null>(null);
  return (
    <ScrollContainerContext.Provider value={ref}>
      {children}
    </ScrollContainerContext.Provider>
  );
};

/**
 * Returns the ref to the app scroll container. Throws when used outside
 * `ScrollContainerProvider` so a missing provider fails loudly rather than
 * silently falling back to (the now non-scrolling) document.
 */
export const useScrollContainerRef = (): ScrollContainerRef => {
  const ref = useContext(ScrollContainerContext);
  if (!ref) {
    throw new Error(
      'useScrollContainerRef must be used within a ScrollContainerProvider',
    );
  }
  return ref;
};
